import { useMemo, useRef, useState, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { getConfluenceContentAri } from './getConfluenceContentAri.ts';

const REPLAY_WINDOW_HOURS = 48;

// helper hook to generate the values needed for the subscription
// - to keep track of last received message timestamp and update the extensions for replay
// - to update the extensions when the subscription is complete
// - to generate the content ARI based on the contentId and contentType
export const useSubscriptionValues = ({
	contentId,
	contentType,
	replayCap,
}: {
	contentId?: string | null;
	contentType?: string | null;
	replayCap?: number;
}) => {
	const { activationId, cloudId } = useSessionData();
	const lastSequenceId = useRef(Math.floor(new Date().getTime() / 1000));
	const [updateExtensionsTrigger, setUpdateExtensionsTrigger] = useState(0);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onSubscriptionComplete = useCallback(() => {
		setUpdateExtensionsTrigger(updateExtensionsTrigger + 1);
	}, [updateExtensionsTrigger, setUpdateExtensionsTrigger]);

	const onSubscriptionData = useCallback(
		(result: any) => {
			// update the last received message timestamp with either the messages time or use a best guess from the current time
			lastSequenceId.current =
				result?.extensions?.sequenceNumber ?? Math.floor(new Date().getTime() / 1000);

			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'received',
					actionSubject: 'subscription.event',
					source: 'AGGSubscriptions',
					attributes: {
						contentId,
						contentType,
						request_id: result?.extensions?.agg?.request_id || '',
						traceId: result?.extensions?.traceId || '',
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, contentId, contentType],
	);
	const ari =
		!contentId || !contentType || !cloudId || !activationId
			? ''
			: getConfluenceContentAri({
					siteId: cloudId,
					resourceType: contentType,
					activationId,
					resourceId: contentId,
				});

	const extensions = useMemo(() => {
		const fallbackReplayCapInSeconds = Math.floor(
			(Date.now() - REPLAY_WINDOW_HOURS * 60 * 60 * 1000) / 1000,
		);
		let sequenceNumber;
		if (replayCap !== undefined) {
			sequenceNumber = replayCap;
		} else if (lastSequenceId.current < fallbackReplayCapInSeconds) {
			sequenceNumber = fallbackReplayCapInSeconds;
		} else {
			sequenceNumber = lastSequenceId.current;
		}

		return updateExtensionsTrigger > 0
			? {
					command: {
						type: 'replay',
						data: {
							sequenceNumber,
						},
					},
				}
			: {};
	}, [updateExtensionsTrigger, replayCap]);

	return { id: ari, extensions, onSubscriptionComplete, onSubscriptionData };
};
